import { useCallback, useMemo, useState } from 'react';
import { GlobalButton } from '../components/GlobalButton';
import { GlobalDatepicker } from '../components/GlobalDatepicker';
import { GlobalDropdown } from '../components/GlobalDropdown';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalInput } from '../components/GlobalInput';
import { GlobalTextarea } from '../components/GlobalTextarea';
import GlobalSidebar from '../components/GlobalSidebar';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import {
  generateDividendShareholderResolution,
  generateDividendShareholderResolutionErrorMessage,
} from '../lib/generateDividendShareholderResolution';
import {
  SKILL_CATEGORIES,
  SKILLS,
  skillById,
  type SkillDef,
  type SkillParamDef,
} from '../lib/skillsRegistry';
import authStyles from '../pages/auth/authPages.module.css';

type SkillResult = {
  skillId: string;
  kind: 'success' | 'error';
  message: string;
  downloadUrl?: string;
  fileName?: string;
};

function SkillIcon({ icon, isDarkMode }: { icon: string; isDarkMode: boolean }) {
  const src = isDarkMode
    ? `/assets/icons/${icon}.svg`
    : `/assets/icons/${icon}.black.svg`;
  return (
    <img src={src} alt="" width={22} height={22} className="skill-card-icon-img" />
  );
}

function SkillCard({
  skill,
  isDarkMode,
  running,
  onRunDirect,
  onOpenSidebar,
}: {
  skill: SkillDef;
  isDarkMode: boolean;
  running: boolean;
  onRunDirect: (skill: SkillDef) => void;
  onOpenSidebar: (skill: SkillDef) => void;
}) {
  const actionLabel = skill.buttonLabel ?? (skill.mode === 'sidebar' ? 'Configure & run' : 'Run');

  return (
    <article className="skill-card" aria-labelledby={`skill-${skill.id}-title`}>
      <div className="skill-card-head">
        <div className="skill-card-icon" aria-hidden="true">
          <SkillIcon icon={skill.icon} isDarkMode={isDarkMode} />
        </div>
        <div className="skill-card-body">
          <p className="skill-card-category">{skill.category}</p>
          <h3 id={`skill-${skill.id}-title`} className="skill-card-title">
            {skill.title}
          </h3>
          <p className="muted skill-card-description">{skill.description}</p>
        </div>
      </div>
      <div className="skill-card-actions">
        {skill.mode === 'sidebar' ? (
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            disabled={running}
            onClick={() => onOpenSidebar(skill)}
          >
            Configure & run
          </GlobalButton>
        ) : (
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            isLoading={running}
            disabled={running}
            onClick={() => onRunDirect(skill)}
          >
            {actionLabel}
          </GlobalButton>
        )}
      </div>
    </article>
  );
}

function defaultParamValue(param: SkillParamDef): string {
  if (param.kind === 'date' && param.key === 'resolutionDate') {
    return new Date().toISOString().slice(0, 10);
  }
  return '';
}

export function SkillsView() {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { bankAccounts, legalEntities } = useAppData();

  const [runningSkillId, setRunningSkillId] = useState<string | null>(null);
  const [result, setResult] = useState<SkillResult | null>(null);
  const [sidebarSkillId, setSidebarSkillId] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const sidebarSkill = sidebarSkillId ? skillById(sidebarSkillId) : undefined;
  const hasSkills = SKILLS.length > 0;

  const bankAccountOptions = useMemo(
    () =>
      (bankAccounts ?? []).map((account) => ({
        value: account.id,
        label: account.name?.trim() || account.ibanMasked?.trim() || account.id,
      })),
    [bankAccounts]
  );

  const legalEntityOptions = useMemo(
    () =>
      (legalEntities ?? [])
        .filter((entity) => !entity.archivedAt)
        .map((entity) => ({
          value: entity.id,
          label: [entity.name, entity.legalForm].filter(Boolean).join(' · '),
        })),
    [legalEntities]
  );

  const skillsByCategory = useMemo(() => {
    const grouped = new Map<string, SkillDef[]>();
    for (const category of SKILL_CATEGORIES) {
      grouped.set(category, []);
    }
    for (const skill of SKILLS) {
      grouped.get(skill.category)?.push(skill);
    }
    return grouped;
  }, []);

  const runSkill = useCallback(async (skill: SkillDef, params: Record<string, string> = {}) => {
    setRunningSkillId(skill.id);
    setResult(null);

    try {
      switch (skill.id) {
        case 'dividend-shareholder-resolution': {
          const dividendAmountEur = Number(params.dividendAmountEur?.replace(',', '.').trim());
          const generated = await generateDividendShareholderResolution({
            legalEntityId: params.legalEntityId?.trim() ?? '',
            companyRegister: params.companyRegister?.trim() || undefined,
            fiscalYear: params.fiscalYear?.trim() ?? '',
            dividendAmountEur,
            resolutionDate: params.resolutionDate?.trim() || undefined,
            distributionDate: params.distributionDate?.trim() ?? '',
            managingDirector: params.managingDirector?.trim() ?? '',
            shareholdersBlock: params.shareholdersBlock?.trim() ?? '',
          });
          setResult({
            skillId: skill.id,
            kind: 'success',
            message: `Generated ${generated.fileName}.`,
            downloadUrl: generated.downloadUrl,
            fileName: generated.fileName,
          });
          break;
        }
        default:
          setResult({
            skillId: skill.id,
            kind: 'error',
            message: 'This skill is not wired up yet.',
          });
      }
    } catch (err) {
      let message = 'Something went wrong.';
      if (skill.id === 'dividend-shareholder-resolution') {
        message = generateDividendShareholderResolutionErrorMessage(err);
      } else if (err instanceof Error) {
        message = err.message;
      }
      setResult({ skillId: skill.id, kind: 'error', message });
    } finally {
      setRunningSkillId(null);
    }
  }, []);

  function openSidebar(skill: SkillDef) {
    const initial: Record<string, string> = {};
    for (const param of skill.params ?? []) {
      initial[param.key] = defaultParamValue(param);
    }
    setParamValues(initial);
    setSidebarSkillId(skill.id);
    setResult(null);
  }

  function closeSidebar() {
    if (runningSkillId) return;
    setSidebarSkillId(null);
    setParamValues({});
  }

  function handleParamChange(key: string, value: string) {
    setParamValues((prev) => ({ ...prev, [key]: value }));
  }

  function resolveParamOptions(param: SkillParamDef) {
    if (param.optionsKey === 'bankAccounts') {
      return bankAccountOptions;
    }
    if (param.optionsKey === 'legalEntities') {
      return legalEntityOptions;
    }
    return [];
  }

  async function handleSidebarSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!sidebarSkill) return;

    for (const param of sidebarSkill.params ?? []) {
      if (param.required && !paramValues[param.key]?.trim()) {
        setResult({
          skillId: sidebarSkill.id,
          kind: 'error',
          message: `${param.label} is required.`,
        });
        return;
      }
    }

    await runSkill(sidebarSkill, paramValues);
    setSidebarSkillId(null);
    setParamValues({});
  }

  return (
    <ViewPage title="Skills">
      <ViewSectionHeader
        eyebrow="Automations"
        title="Skills"
        subtitle="Run backend automations directly or configure parameters before triggering."
      />

      {result ? (
        <div
          className={`${authStyles.banner} ${
            result.kind === 'success' ? authStyles.bannerSuccess : authStyles.bannerError
          } skill-result-banner`}
          role={result.kind === 'success' ? 'status' : 'alert'}
        >
          <p style={{ margin: 0 }}>{result.message}</p>
          {result.kind === 'success' && result.downloadUrl ? (
            <GlobalButton
              type="button"
              variant="secondary"
              autoWidth
              style={{ marginTop: 10 }}
              onClick={() => window.open(result.downloadUrl, '_blank', 'noopener,noreferrer')}
            >
              Download {result.fileName ?? 'PDF'}
            </GlobalButton>
          ) : null}
        </div>
      ) : null}

      {!hasSkills ? (
        <GlobalEmptyState />
      ) : (
        SKILL_CATEGORIES.map((category) => {
          const skills = skillsByCategory.get(category) ?? [];
          if (skills.length === 0) return null;

          return (
            <section key={category} className="skills-category" aria-labelledby={`skills-${category}`}>
              <h2 id={`skills-${category}`} className="skills-category-title">
                {category}
              </h2>
              <div className="skills-grid">
                {skills.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    isDarkMode={isDarkMode}
                    running={runningSkillId === skill.id}
                    onRunDirect={runSkill}
                    onOpenSidebar={openSidebar}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      <GlobalSidebar isOpen={Boolean(sidebarSkill)} onClose={closeSidebar}>
        {sidebarSkill ? (
          <form className="sidebarFormStack" onSubmit={handleSidebarSubmit}>
            <h2 className="sidebarFormTitle">{sidebarSkill.title}</h2>
            <p className="muted">{sidebarSkill.description}</p>

            {(sidebarSkill.params ?? []).map((param) => {
              if (param.kind === 'select') {
                const options = resolveParamOptions(param);
                return (
                  <GlobalDropdown
                    key={param.key}
                    label={param.label}
                    value={paramValues[param.key] ?? ''}
                    onChange={(value) => handleParamChange(param.key, value)}
                    options={options}
                    placeholder={param.placeholder ?? 'Select'}
                    disabled={runningSkillId === sidebarSkill.id}
                    required={param.required}
                  />
                );
              }

              if (param.kind === 'textarea') {
                return (
                  <GlobalTextarea
                    key={param.key}
                    label={param.label}
                    value={paramValues[param.key] ?? ''}
                    onChange={(event) => handleParamChange(param.key, event.target.value)}
                    placeholder={param.placeholder}
                    disabled={runningSkillId === sidebarSkill.id}
                    required={param.required}
                    rows={5}
                  />
                );
              }

              if (param.kind === 'date') {
                return (
                  <GlobalDatepicker
                    key={param.key}
                    label={param.label}
                    value={paramValues[param.key] ?? ''}
                    onChange={(event) => handleParamChange(param.key, event.target.value)}
                    disabled={runningSkillId === sidebarSkill.id}
                    required={param.required}
                  />
                );
              }

              return (
                <GlobalInput
                  key={param.key}
                  label={param.label}
                  value={paramValues[param.key] ?? ''}
                  onChange={(event) => handleParamChange(param.key, event.target.value)}
                  placeholder={param.placeholder}
                  disabled={runningSkillId === sidebarSkill.id}
                  required={param.required}
                />
              );
            })}

            <div className="sidebarFormActions">
              <GlobalButton
                type="button"
                variant="secondary"
                onClick={closeSidebar}
                disabled={runningSkillId === sidebarSkill.id}
              >
                Cancel
              </GlobalButton>
              <GlobalButton
                type="submit"
                variant="primary"
                isLoading={runningSkillId === sidebarSkill.id}
                disabled={runningSkillId === sidebarSkill.id}
              >
                {sidebarSkill.buttonLabel ?? 'Run'}
              </GlobalButton>
            </div>
          </form>
        ) : null}
      </GlobalSidebar>
    </ViewPage>
  );
}
